import React, { useEffect, useMemo, useState } from "react";
import InlineLoading from "./InlineLoading";

const buildAvailabilityState = (entries) => {
  const next = {};
  if (!Array.isArray(entries)) return next;
  entries.forEach((entry) => {
    const branchId = entry?.branchId;
    if (!branchId) return;
    const key = String(branchId);
    if (!next[key]) {
      next[key] = { all: false, cashRegisterIds: [] };
    }
    if (!entry.cashRegisterId) {
      next[key].all = true;
    } else {
      const id = String(entry.cashRegisterId);
      if (!next[key].cashRegisterIds.includes(id)) {
        next[key].cashRegisterIds.push(id);
      }
    }
  });
  return next;
};

const CategoryFormModal = ({
  isOpen,
  onClose,
  onSave,
  branches,
  cashRegistersByBranch,
  initialData,
  isSaving,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [availability, setAvailability] = useState({});
  const [error, setError] = useState("");

  const availabilityEntries = useMemo(() => {
    return (
      initialData?.CategoryAvailabilities
      || initialData?.CategoryAvailability
      || []
    );
  }, [initialData]);

  useEffect(() => {
    if (!isOpen) return;
    setError("");
    setName(initialData?.name || "");
    setDescription(initialData?.description || "");
    setIsActive(initialData?.isActive ?? true);
    setAvailability(buildAvailabilityState(availabilityEntries));
  }, [availabilityEntries, initialData, isOpen]);

  if (!isOpen) return null;

  const updateBranchAvailability = (branchId, updater) => {
    setAvailability((prev) => {
      const key = String(branchId);
      const current = prev[key] || { all: false, cashRegisterIds: [] };
      const nextValue = updater({ ...current });
      const next = { ...prev };
      if (!nextValue.all && nextValue.cashRegisterIds.length === 0) {
        delete next[key];
      } else {
        next[key] = nextValue;
      }
      return next;
    });
  };

  const toggleBranchAll = (branchId) => {
    updateBranchAvailability(branchId, (current) => {
      const nextAll = !current.all;
      return { all: nextAll, cashRegisterIds: nextAll ? [] : current.cashRegisterIds };
    });
  };

  const toggleCashRegister = (branchId, cashRegisterId) => {
    updateBranchAvailability(branchId, (current) => {
      const id = String(cashRegisterId);
      const ids = current.cashRegisterIds.includes(id)
        ? current.cashRegisterIds.filter((item) => item !== id)
        : [...current.cashRegisterIds, id];
      return { all: false, cashRegisterIds: ids };
    });
  };

  const buildAvailabilityPayload = () => {
    const payload = [];
    Object.entries(availability).forEach(([branchId, selection]) => {
      const resolvedBranchId = Number(branchId);
      if (!Number.isInteger(resolvedBranchId)) return;
      if (selection.all) {
        payload.push({ branchId: resolvedBranchId, cashRegisterId: null, isActive: true });
        return;
      }
      selection.cashRegisterIds.forEach((cashRegisterId) => {
        const resolvedId = Number(cashRegisterId);
        if (!Number.isInteger(resolvedId)) return;
        payload.push({ branchId: resolvedBranchId, cashRegisterId: resolvedId, isActive: true });
      });
    });
    return payload;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError("El nombre es obligatorio.");
      return;
    }
    const payload = {
      name: trimmedName,
      description: description.trim() || null,
      isActive: Boolean(isActive),
      availability: buildAvailabilityPayload(),
    };
    onSave(payload);
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[1000] flex justify-center items-start overflow-y-auto px-4 py-8"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-4xl rounded-2xl shadow-2xl border border-gray-100 overflow-hidden max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500">Categorías</p>
            <h2 className="text-lg font-semibold text-gray-900">
              {initialData ? "Editar categoria" : "Nueva categoria"}
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              Define nombre, estado y disponibilidad por sucursal y caja.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-gray-900 bg-white border border-gray-200 rounded-full w-9 h-9 flex items-center justify-center"
          >
            ✕
          </button>
        </div>

        <form className="p-6 space-y-6" onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase">Nombre</label>
              <input
                type="text"
                className="mt-2 w-full border border-gray-300 rounded-lg p-2"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="flex items-end">
              <label className="inline-flex items-center gap-2 text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 w-full">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
                Categoria activa
              </label>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase">Descripcion</label>
            <textarea
              rows={2}
              className="mt-2 w-full border border-gray-300 rounded-lg p-2"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase">
                  Disponibilidad por sucursal y caja
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  Marca “Todas las cajas” o selecciona cajas específicas.
                </p>
              </div>
            </div>
            <div className="space-y-3">
              {branches.map((branch) => {
                const branchKey = String(branch.id);
                const branchSelection = availability[branchKey] || { all: false, cashRegisterIds: [] };
                const registers = cashRegistersByBranch[branch.id] || [];
                return (
                  <div key={branch.id} className="border border-gray-200 rounded-xl p-4 bg-white shadow-sm">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold text-gray-800">{branch.branchName}</h4>
                      <label className="flex items-center gap-2 text-xs text-gray-600">
                        <input
                          type="checkbox"
                          checked={branchSelection.all}
                          onChange={() => toggleBranchAll(branch.id)}
                        />
                        Todas las cajas
                      </label>
                    </div>
                    {registers.length === 0 ? (
                      <p className="text-xs text-gray-500 mt-2">
                        Sin cajas registradas.
                      </p>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3">
                        {registers.map((register) => (
                          <label key={register.id} className="flex items-center gap-2 text-xs text-gray-600">
                            <input
                              type="checkbox"
                              checked={branchSelection.cashRegisterIds.includes(String(register.id))}
                              onChange={() => toggleCashRegister(branch.id, register.id)}
                              disabled={branchSelection.all}
                            />
                            {register.name}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex items-center justify-between border-t border-gray-100 pt-4">
            <div className="text-xs text-gray-500">
              Los cambios aplican inmediatamente en el catálogo.
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:text-gray-900"
                onClick={onClose}
                disabled={isSaving}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                disabled={isSaving}
              >
                {isSaving ? <InlineLoading label="Guardando" /> : "Guardar"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CategoryFormModal;
